"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
export default function ConsultingComparison() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const metrics = [{
    label: "OTA VISIBILITY",
    value: "45%",
    direction: "up"
  }, {
    label: "REVENUE LEAKAGE",
    value: "30%",
    direction: "down"
  }, {
    label: "DIRECT BOOKINGS",
    value: "120%",
    direction: "up"
  }];
  return <section data-section-id="1261" id="consultingcomparison" className="bg-slate-50 text-slate-900 py-28 md:py-40 px-6 md:px-16 font-default overflow-hidden">
      <div ref={ref} className={`max-w-[1320px] mx-auto transition-all duration-[720ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
      transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
    }}>
        {}
        <div className="flex flex-col md:flex-row md:items-start gap-8 md:gap-12 mb-16 md:mb-24">
          {}
          <div className="flex md:flex-col items-center md:items-start gap-4 md:gap-3 md:pt-2 shrink-0">
            <span className="font-default text-2xl text-blue-900/80" style={{
            fontWeight: 300
          }}>
              HR
            </span>
            <span className="font-sans text-[0.6875rem] md:text-[0.8125rem] uppercase text-blue-900/60" style={{
            letterSpacing: "0.22em"
          }}>
              02 — REVENUE IMPACT
            </span>
          </div>

          {}
          <div className="flex-1 md:pl-8 md:border-l md:border-blue-200">
            <h2 className="font-default text-blue-950" style={{
            fontSize: "clamp(2.125rem, 4.4vw, 4rem)",
            fontWeight: 600,
            letterSpacing: "-0.025em",
            lineHeight: 1.04
          }}>
              From fragmented{" "}
              <span className="font-default" style={{
              fontStyle: "italic",
              fontWeight: 400
            }}>
                operations
              </span>{" "}
              to optimized{" "}
              <span className="font-default" style={{
              fontStyle: "italic",
              fontWeight: 400
            }}>
                profitability
              </span>
              .
            </h2>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden md:flex justify-between mb-6 px-1">
            <span className="font-sans text-[0.8125rem] uppercase text-slate-600" style={{
            letterSpacing: "0.22em"
          }}>
              BEFORE — UNOPTIMIZED
            </span>
            <span className="font-sans text-[0.8125rem] uppercase text-slate-600" style={{
            letterSpacing: "0.22em"
          }}>
              AFTER — REVENUE GROWTH
            </span>
          </div>

          {}
          <div className="hidden md:block relative" style={{
          aspectRatio: "16 / 9"
        }}>
            {}
            <span className="absolute -top-2 -left-2 w-3 h-3 border-t border-l border-blue-900/40 z-20" />
            <span className="absolute -top-2 -right-2 w-3 h-3 border-t border-r border-blue-900/40 z-20" />
            <span className="absolute -bottom-2 -left-2 w-3 h-3 border-b border-l border-blue-900/40 z-20" />
            <span className="absolute -bottom-2 -right-2 w-3 h-3 border-b border-r border-blue-900/40 z-20" />

            <ResizablePanelGroup direction="horizontal" className="h-full w-full">
              <ResizablePanel defaultSize={50} minSize={10}>
                <div className="relative h-full w-full overflow-hidden rounded-lg shadow-md">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_inefficiency.png" alt="Disorganized hotel front desk and manual revenue tracking" loading="lazy" decoding="async" className="h-full w-full object-cover" style={{
                  filter: "grayscale(0.9) sepia(0.15) contrast(0.95) brightness(0.85)"
                }} />
                  <div className="absolute inset-0 bg-blue-900/10 mix-blend-overlay pointer-events-none" />
                </div>
              </ResizablePanel>
              <ResizableHandle className="!w-px !bg-blue-900/80 relative after:absolute after:inset-y-0 after:left-1/2 after:w-8 after:-translate-x-1/2 after:bg-transparent">
                <div className="z-10 flex h-8 w-8 items-center justify-center rounded-lg border border-blue-900/80 bg-white">
                  <span className="block h-1.5 w-1.5 rounded-lg bg-amber-500" />
                </div>
              </ResizableHandle>
              <ResizablePanel defaultSize={50} minSize={10}>
                <div className="relative h-full w-full overflow-hidden rounded-lg shadow-md">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_growth.png" alt="Modern hotel dashboard showing revenue growth and analytics" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>

          {}
          <div className="md:hidden flex flex-col gap-3">
            <div className="relative">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="font-sans text-[0.6875rem] uppercase text-slate-600" style={{
                letterSpacing: "0.22em"
              }}>
                  BEFORE — UNOPTIMIZED
                </span>
              </div>
              <div className="relative overflow-hidden rounded-lg shadow-md" style={{
              aspectRatio: "4 / 3"
            }}>
                <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-blue-900/40 z-20" />
                <span className="absolute top-0 right-0 w-2 h-2 border-t border-r border-blue-900/40 z-20" />
                <span className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-blue-900/40 z-20" />
                <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-blue-900/40 z-20" />
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_inefficiency.png" alt="Disorganized hotel front desk and manual revenue tracking" loading="lazy" decoding="async" className="h-full w-full object-cover" style={{
                filter: "grayscale(0.9) sepia(0.15) contrast(0.95) brightness(0.85)"
              }} />
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 py-2">
              <span className="h-px flex-1 bg-blue-900/30" />
              <span className="block h-1.5 w-1.5 rounded-lg bg-amber-500" />
              <span className="h-px flex-1 bg-blue-900/30" />
            </div>

            <div className="relative">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="font-sans text-[0.6875rem] uppercase text-slate-600" style={{
                letterSpacing: "0.22em"
              }}>
                  AFTER — REVENUE GROWTH
                </span>
              </div>
              <div className="relative overflow-hidden rounded-lg shadow-md" style={{
              aspectRatio: "4 / 3"
            }}>
                <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-blue-900/40 z-20" />
                <span className="absolute top-0 right-0 w-2 h-2 border-t border-r border-blue-900/40 z-20" />
                <span className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-blue-900/40 z-20" />
                <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-blue-900/40 z-20" />
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_growth.png" alt="Modern hotel dashboard showing revenue growth and analytics" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20 pt-10 border-t border-blue-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-8 md:gap-y-0">
            {metrics.map((metric, i) => <div key={i} data-index={i} className={`flex flex-col items-center text-center md:px-6 ${i < metrics.length - 1 ? "md:border-r md:border-blue-200" : ""}`}>
                <span className="font-sans text-[0.75rem] uppercase text-blue-900/60 mb-3" style={{
              letterSpacing: "0.22em"
            }}>
                  {metric.label}
                </span>
                <div className="flex items-baseline gap-2">
                  <span className="font-sans text-xl md:text-2xl" style={{
                color: "var(--secondary)",
                letterSpacing: "0.05em"
              }}>
                    {metric.direction === "down" ? "↓" : "↑"}
                  </span>
                  <span className="font-default text-2xl md:text-3xl text-blue-950" style={{
                fontWeight: 600,
                letterSpacing: "-0.02em"
              }}>
                    {metric.value}
                  </span>
                </div>
              </div>)}
          </div>

          {}
          <div className="mt-12 flex items-center gap-6">
            <span className="h-px flex-1 bg-blue-900/20" />
            <span className="font-sans text-[0.75rem] uppercase text-blue-900/50 whitespace-nowrap" style={{
            letterSpacing: "0.16em"
          }}>
              DATA-DRIVEN STRATEGY
            </span>
            <span className="h-px flex-1 bg-blue-900/20" />
            <span className="block h-1.5 w-1.5 rounded-lg shrink-0 bg-amber-500" />
          </div>
        </div>
      </div>
    </section>;
}